import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { commonDescendingComparator, momentDescendingComparator } from 'utils/helper';
import { isUnscoredRubric } from 'utils/rubricScoring';

export const getRubricTemplateTotalPoints = (rubric) => {
    if (isUnscoredRubric(rubric)) return null;

    return rubric.criteria.reduce((acc, cur) => {
        return acc + Math.max(...cur.options.map(({ rate }) => Number(rate)));
    }, 0);
};

export const sortRubricTemplatesByPoints = (rubrics, order) => {
    if (!order) return rubrics;

    return [...rubrics].sort((a, b) => {
        const pointsA = getRubricTemplateTotalPoints(a.rubric);
        const pointsB = getRubricTemplateTotalPoints(b.rubric);

        // Unscored rubrics do not have a numeric value to compare. Keep them
        // after scored rubrics and preserve their relative order.
        if (pointsA === null && pointsB === null) return 0;
        if (pointsA === null) return 1;
        if (pointsB === null) return -1;

        const comparison = commonDescendingComparator(a, b, ({ rubric }) => {
            return getRubricTemplateTotalPoints(rubric);
        });
        return order === 'desc' ? comparison : -comparison;
    });
};

export const useRubricTableColumns = () => {
    const { t } = useTranslation();

    return [
        {
            id: 'name',
            columnTitle: t('table.name', 'Name'),
            comparator: (order, rubrics, _) => {
                if (!order) return rubrics;
                const getter = (r) => {
                    return r.rubric.title.toLowerCase();
                };

                if (order === 'desc') {
                    const sortedRubrics = [...rubrics].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedRubrics;
                }
                const sortedRubrics = [...rubrics].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedRubrics;
            },
        },
        {
            id: 'lastupdated',
            columnTitle: t('table.last-updated', 'Last Updated'),
            comparator: (order, rubrics, _) => {
                if (!order) return rubrics;
                const getter = (r) => {
                    if (!r.modified_on) return null;
                    return moment(r.modified_on);
                };

                if (order === 'desc') {
                    const sortedRubrics = [...rubrics].sort((a, b) => momentDescendingComparator(a, b, getter));
                    return sortedRubrics;
                }
                const sortedRubrics = [...rubrics].sort((a, b) => -momentDescendingComparator(a, b, getter));
                return sortedRubrics;
            },
        },
        {
            id: 'criteria',
            columnTitle: t('table.criteria', 'Criteria'),
            ascSuffix: '(asc)',
            descSuffix: '(desc)',
            comparator: (order, rubrics, _) => {
                if (!order) return rubrics;
                const getter = (r) => {
                    return r.rubric.criteria.length;
                };

                if (order === 'desc') {
                    const sortedRubrics = [...rubrics].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedRubrics;
                }
                const sortedRubrics = [...rubrics].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedRubrics;
            },
        },
        {
            id: 'points',
            columnTitle: t('table.points', 'Points'),
            ascSuffix: '(asc)',
            descSuffix: '(desc)',
            comparator: (order, rubrics, _) => {
                return sortRubricTemplatesByPoints(rubrics, order);
            },
        },
    ];
};
